import { Dropdown, App, Segmented } from 'antd'
import * as Sentry from '@sentry/react'
import { LogoutOutlined, UserOutlined, SunOutlined, MoonOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { useEffect } from 'react'
import { AuthService } from '../../services/auth/authService'
import { useProfileContext } from '../../context/profile'
import { useAuthContext } from '../../context/auth'
import { useThemeContext } from '../../context/theme'
import { getUserInitials } from '../../utils/userInitials'
import type { ThemeMode } from '@bandos/shared'
import {
  DropdownContainer,
  UserInfoSpace,
  UserNameContainer,
  UserNameText,
  RoleTag,
  UserEmailText,
  MenuDivider,
  MenuButtonsContainer,
  MenuButton,
  LastMenuButton,
  StyledAvatar,
  ThemeSection,
  ThemeSectionLabel,
} from './styles/UserMenuStyles'

export function UserMenu() {
  const navigate = useNavigate()
  const { profile, updateProfile } = useProfileContext()
  const { user } = useAuthContext()
  const { notification } = App.useApp()
  const { theme, setTheme, syncFromProfile } = useThemeContext()

  // Sync theme from profile when it loads
  useEffect(() => {
    if (profile?.theme) {
      syncFromProfile(profile.theme)
    }
  }, [profile?.theme, syncFromProfile])

  const handleThemeChange = async (value: string | number) => {
    const newTheme = value as ThemeMode
    setTheme(newTheme)
    // Save to profile in background
    try {
      await updateProfile({ theme: newTheme })
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: 'navigation', action: 'update_theme' },
      })
      // Theme is already applied locally via localStorage, so no need to revert
    }
  }

  const handleSignOut = async () => {
    const { error } = await AuthService.signOut()
    if (error) {
      notification.error({
        title: 'Sign Out Failed',
        description: error.message,
      })
    } else {
      notification.success({
        title: 'Signed Out',
        description: 'You have been successfully signed out',
      })
      void navigate('/login')
    }
  }

  const handleSettingsClick = () => {
    void navigate('/settings?section=account')
  }

  const initials = getUserInitials(profile?.full_name)

  const dropdownContent = (
    <DropdownContainer>
      <UserInfoSpace orientation="vertical" size={4}>
        <UserNameContainer>
          <UserNameText strong>{profile?.full_name ?? 'User'}</UserNameText>
          {profile?.role && (profile.role === 'admin' || profile.role === 'super_admin') && (
            <RoleTag color="purple">
              {profile.role === 'super_admin' ? 'Super Admin' : 'Admin'}
            </RoleTag>
          )}
        </UserNameContainer>
        <UserEmailText type="secondary">{user?.email ?? ''}</UserEmailText>
      </UserInfoSpace>

      <MenuDivider />

      <ThemeSection>
        <ThemeSectionLabel>Theme</ThemeSectionLabel>
        <Segmented
          value={theme}
          onChange={handleThemeChange}
          options={[
            {
              label: (
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <SunOutlined /> Light
                </span>
              ),
              value: 'light',
            },
            {
              label: (
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <MoonOutlined /> Dark
                </span>
              ),
              value: 'dark',
            },
          ]}
          block
        />
      </ThemeSection>

      <MenuDivider />

      <MenuButtonsContainer>
        <MenuButton type="text" icon={<UserOutlined />} onClick={handleSettingsClick}>
          Profile Settings
        </MenuButton>
        <LastMenuButton type="text" danger icon={<LogoutOutlined />} onClick={handleSignOut}>
          Sign Out
        </LastMenuButton>
      </MenuButtonsContainer>
    </DropdownContainer>
  )

  return (
    <Dropdown popupRender={() => dropdownContent} placement="bottomRight" trigger={['click']}>
      <StyledAvatar size="default">{initials}</StyledAvatar>
    </Dropdown>
  )
}
